import { InstantSearch } from 'react-instantsearch';

import InstantSearchFilterOption from './units/instantSearchFilterOption';

import { useMultiSelect } from '@javascript/components/search_filters/hooks';
import useRelatedRefinementList from './custom/useRelatedRefinementList';
import { useSearchClient } from '../provider/searchClientProvider';

// the algolia hooks can only be called inside an InstantSearch context,
// hence this is a separate component
const AdditionalMakesList = ({
  handleChange,
  isSelected,
}: {
  handleChange: (value: string) => void;
  isSelected: (value: string) => boolean;
}) => {
  const { items } = useRelatedRefinementList({
    attribute: 'makeModel.make',
    relatedAttributes: [],
  });

  return (
    <ul>
      {items.map(({ name, escapedValue }) => (
        <InstantSearchFilterOption.CheckBox
          id={`${name}_additional-make`}
          isSelected={isSelected(escapedValue)}
          key={`additional-make__${name}`}
          label={escapedValue}
          onChange={() => handleChange(escapedValue)}
        />
      ))}
    </ul>
  );
};

const AdditionalMakesFilter = ({
  closeFilter,
  currentSelectedMakes,
  updateSelectedMakes,
}: {
  closeFilter: () => void;
  currentSelectedMakes: string[];
  updateSelectedMakes: (value: string | string[]) => void;
}) => {
  // use these to create a seperate instantSearch instance locally scoped to this component
  // that can't be affected by the main search
  const { indexName, searchClient } = useSearchClient();

  const {
    handleChange: updateAdditionalMakes,
    selectedOptions: additionalMakes,
    resetSelected: resetAdditionalMakes,
  } = useMultiSelect();

  const handleSubmit = () => {
    updateSelectedMakes(additionalMakes);
    resetAdditionalMakes();
    closeFilter();
  };

  // if the make is already selected, remove it from the main search
  const handleChange = (make: string) => {
    currentSelectedMakes.includes(make)
      ? updateSelectedMakes(make)
      : updateAdditionalMakes(make);
  };

  const isSelected = (make: string) => {
    return (
      currentSelectedMakes.includes(make) || additionalMakes.includes(make)
    );
  };

  return (
    <InstantSearch indexName={indexName} searchClient={searchClient}>
      <div className="instant-search-modal ac-additional-makes ch-modal ch-modal--drawer">
        <header>
          <div className="ch-ph--2 sl:ch-ph--6 ch-pt--2 ch-pb--3 ch-display--flex ch-flex-flow--row-wrap ch-justify-content--between ch-align-items--center ch-width--12">
            <button
              className="ac-back-button ch-ba--0 ch-color--ac-black ch-mr--auto ch-bg--grey-2 ch-rounded--md ch-pv--1 ch-ph--2 ch-fs--3 ch-mv--3"
              onClick={closeFilter}
              type="button"
            >
              Back
            </button>
            <h1 className="ch-mb--0 ch-fs--5 ch-fw--500">
              Add additional makes to your search
            </h1>
          </div>
        </header>
        <main className="ch-modal__body">
          <div className="ch-ph--2 sl:ch-ph--6">
            <AdditionalMakesList
              handleChange={handleChange}
              isSelected={isSelected}
            />
          </div>
        </main>
        {additionalMakes.length > 0 && (
          <div className="filter__footer ch-display--flex ch-flex-direction--column ch-align-items--center">
            <button
              className="ch-btn ch-btn--primary ch-width--12"
              data-gtm-track="click_cta"
              data-gtm-track-label={`Add makes (${additionalMakes.length})`}
              onClick={handleSubmit}
              type="button"
            >
              Add makes ({additionalMakes.length})
            </button>
          </div>
        )}
      </div>
    </InstantSearch>
  );
};

export default AdditionalMakesFilter;
